/**
 * Paciente Form Dialog - Create/Edit Patient
 *
 * Formulario para crear o editar pacientes.
 * Usa TanStack Query para las mutaciones (useCreatePaciente, useUpdatePaciente).
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ImageCapture } from "@/components/ui/image-capture";
import { LocationSelector } from "@/components/ui/location-selector";
import { DatePicker } from "@/components/ui/date-picker";

import { useApiError } from "@/hooks/use-api-error";
import {
  useCreatePaciente,
  useUpdatePaciente,
  type Paciente,
  type TipoDocumento,
  type Sexo,
} from "@/hooks/use-pacientes";

interface PacienteFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paciente: Paciente | null;
  onSuccess: (
    isNew: boolean,
    createdPaciente?: {
      id: number;
      nombre_completo: string;
      identificacion?: string;
      edad_texto?: string;
      edad_years?: number;
      celular?: string;
    }
  ) => void;
}

const TIPOS_DOCUMENTO: { value: TipoDocumento; label: string }[] = [
  { value: "DNI", label: "DNI / Identidad" },
  { value: "PASAPORTE", label: "Pasaporte" },
  { value: "PARTIDA_NACIMIENTO", label: "Partida de Nacimiento" },
  { value: "SIN_IDENTIFICACION", label: "Sin Identificación" },
];

const SEXOS: { value: Sexo; labelKey: string }[] = [
  { value: "M", labelKey: "pacientes.gender.male" },
  { value: "F", labelKey: "pacientes.gender.female" },
];

// Schema de validación
const pacienteSchema = z
  .object({
    tipo_documento: z.enum(["DNI", "PASAPORTE", "PARTIDA_NACIMIENTO", "SIN_IDENTIFICACION"]),
    num_documento: z.string().optional(),
    primer_nombre: z.string().min(2, "pacientes.validation.firstNameRequired"),
    segundo_nombre: z.string().optional(),
    primer_apellido: z.string().min(2, "pacientes.validation.lastNameRequired"),
    segundo_apellido: z.string().optional(),
    fecha_nacimiento: z.string().min(1, "pacientes.validation.birthDateRequired"),
    sexo: z.enum(["M", "F"]),
    departamento: z.string().optional(),
    ciudad: z.string().optional(),
    direccion: z.string().optional(),
    telefono: z.string().optional(),
    celular: z.string().optional(),
    tiene_whatsapp: z.boolean(),
    email: z.string().email("pacientes.validation.emailInvalid").optional().or(z.literal("")),
  })
  .refine(
    (data) => {
      // Si no es "Sin Identificación", el número de documento es requerido
      if (data.tipo_documento !== "SIN_IDENTIFICACION") {
        return data.num_documento && data.num_documento.length >= 1;
      }
      return true;
    },
    {
      message: "pacientes.validation.documentRequired",
      path: ["num_documento"],
    }
  );

type FormData = z.infer<typeof pacienteSchema>;

export function PacienteFormDialog({
  open,
  onOpenChange,
  paciente,
  onSuccess,
}: PacienteFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [error, setError] = useState<string | null>(null);
  const [fotoDocumento, setFotoDocumento] = useState<File | null>(null);

  const isEditing = !!paciente;

  // TanStack Query mutations
  const createMutation = useCreatePaciente({
    onSuccess: (paciente) =>
      onSuccess(true, {
        id: paciente.id,
        nombre_completo: paciente.nombre_completo,
        identificacion: paciente.identificacion,
        edad_texto: paciente.edad?.texto,
        edad_years: paciente.edad?.years,
        celular: paciente.celular || undefined,
      }),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const updateMutation = useUpdatePaciente({
    onSuccess: () => onSuccess(false),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(pacienteSchema),
    defaultValues: {
      tipo_documento: "DNI",
      num_documento: "",
      primer_nombre: "",
      segundo_nombre: "",
      primer_apellido: "",
      segundo_apellido: "",
      fecha_nacimiento: "",
      sexo: "M",
      departamento: "",
      ciudad: "",
      direccion: "",
      telefono: "",
      celular: "",
      tiene_whatsapp: false,
      email: "",
    },
  });

  const tieneWhatsapp = watch("tiene_whatsapp");
  const tipoDocumento = watch("tipo_documento");
  const sexo = watch("sexo");
  const departamento = watch("departamento");
  const ciudad = watch("ciudad");

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (paciente) {
        reset({
          tipo_documento: paciente.tipo_documento,
          num_documento: paciente.num_documento,
          primer_nombre: paciente.primer_nombre,
          segundo_nombre: paciente.segundo_nombre || "",
          primer_apellido: paciente.primer_apellido,
          segundo_apellido: paciente.segundo_apellido || "",
          fecha_nacimiento: paciente.fecha_nacimiento,
          sexo: paciente.sexo,
          departamento: paciente.departamento || "",
          ciudad: paciente.ciudad || "",
          direccion: paciente.direccion || "",
          telefono: paciente.telefono || "",
          celular: paciente.celular || "",
          tiene_whatsapp: paciente.tiene_whatsapp,
          email: paciente.email || "",
        });
        setFotoDocumento(null); // Reset file, existing photo shown via existingUrl
      } else {
        reset({
          tipo_documento: "DNI",
          num_documento: "",
          primer_nombre: "",
          segundo_nombre: "",
          primer_apellido: "",
          segundo_apellido: "",
          fecha_nacimiento: "",
          sexo: "M",
          departamento: "",
          ciudad: "",
          direccion: "",
          telefono: "",
          celular: "",
          tiene_whatsapp: false,
          email: "",
        });
        setFotoDocumento(null);
      }
      setError(null);
    }
  }, [open, paciente, reset]);

  const onSubmit = (data: FormData) => {
    setError(null);

    const payload = {
      tipo_documento: data.tipo_documento,
      num_documento: data.tipo_documento === "SIN_IDENTIFICACION" ? undefined : data.num_documento,
      primer_nombre: data.primer_nombre,
      segundo_nombre: data.segundo_nombre || undefined,
      primer_apellido: data.primer_apellido,
      segundo_apellido: data.segundo_apellido || undefined,
      fecha_nacimiento: data.fecha_nacimiento,
      sexo: data.sexo,
      departamento: data.departamento || undefined,
      ciudad: data.ciudad || undefined,
      direccion: data.direccion || undefined,
      telefono: data.telefono || undefined,
      celular: data.celular || undefined,
      tiene_whatsapp: data.tiene_whatsapp,
      email: data.email || undefined,
      foto_documento: fotoDocumento || undefined,
    };

    if (isEditing && paciente) {
      updateMutation.mutate({ id: paciente.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t("pacientes.editPatient") : t("pacientes.newPatient")}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t("pacientes.editPatientDesc") : t("pacientes.newPatientDesc")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          <div className="grid gap-4">
            {/* Tipo y Número de Documento */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("pacientes.fields.documentType")}</Label>
                <Select
                  value={tipoDocumento}
                  onValueChange={(value) => {
                    setValue("tipo_documento", value as TipoDocumento);
                    // Limpiar número de documento si se selecciona "Sin Identificación"
                    if (value === "SIN_IDENTIFICACION") {
                      setValue("num_documento", "");
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_DOCUMENTO.map((tipo) => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {tipoDocumento !== "SIN_IDENTIFICACION" && (
                <div className="space-y-2">
                  <Label htmlFor="num_documento">{t("pacientes.fields.documentNumber")}</Label>
                  <Input
                    id="num_documento"
                    placeholder={isEditing ? undefined : "0801-1990-12345"}
                    className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                    {...register("num_documento")}
                  />
                  {errors.num_documento && (
                    <p className="text-sm text-destructive">
                      {t(errors.num_documento.message as string)}
                    </p>
                  )}
                </div>
              )}

              {tipoDocumento === "SIN_IDENTIFICACION" && (
                <div className="space-y-2">
                  <Label>{t("pacientes.fields.documentNumber")}</Label>
                  <div className="flex h-10 items-center rounded-md border bg-muted px-3 text-sm text-muted-foreground">
                    {t("pacientes.autoGeneratedCode")}
                  </div>
                </div>
              )}
            </div>

            {/* Nombres */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primer_nombre">{t("pacientes.fields.firstName")}</Label>
                <Input
                  id="primer_nombre"
                  placeholder={isEditing ? undefined : "Juan"}
                  className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                  {...register("primer_nombre")}
                />
                {errors.primer_nombre && (
                  <p className="text-sm text-destructive">
                    {t(errors.primer_nombre.message as string)}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="segundo_nombre">{t("pacientes.fields.secondName")}</Label>
                <Input
                  id="segundo_nombre"
                  placeholder={isEditing ? undefined : "Carlos"}
                  className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                  {...register("segundo_nombre")}
                />
              </div>
            </div>

            {/* Apellidos */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primer_apellido">{t("pacientes.fields.lastName")}</Label>
                <Input
                  id="primer_apellido"
                  placeholder={isEditing ? undefined : "Pérez"}
                  className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                  {...register("primer_apellido")}
                />
                {errors.primer_apellido && (
                  <p className="text-sm text-destructive">
                    {t(errors.primer_apellido.message as string)}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="segundo_apellido">{t("pacientes.fields.secondLastName")}</Label>
                <Input
                  id="segundo_apellido"
                  placeholder={isEditing ? undefined : "García"}
                  className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                  {...register("segundo_apellido")}
                />
              </div>
            </div>

            {/* Fecha de Nacimiento y Sexo */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("pacientes.fields.birthDate")}</Label>
                <DatePicker
                  value={watch("fecha_nacimiento")}
                  onChange={(value) => setValue("fecha_nacimiento", value)}
                  placeholder={t("pacientes.fields.selectBirthDate", "Seleccionar fecha")}
                />
                {errors.fecha_nacimiento && (
                  <p className="text-sm text-destructive">
                    {t(errors.fecha_nacimiento.message as string)}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>{t("pacientes.fields.gender")}</Label>
                <Select value={sexo} onValueChange={(value) => setValue("sexo", value as Sexo)}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEXOS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {t(s.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Ubicación */}
            <LocationSelector
              departamento={departamento || ""}
              ciudad={ciudad || ""}
              onDepartamentoChange={(value) => setValue("departamento", value)}
              onCiudadChange={(value) => setValue("ciudad", value)}
            />

            {/* Dirección */}
            <div className="space-y-2">
              <Label htmlFor="direccion">{t("pacientes.fields.address")}</Label>
              <Input
                id="direccion"
                placeholder={isEditing ? undefined : "Col. Kennedy, Bloque A, Casa 123"}
                className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                {...register("direccion")}
              />
            </div>

            {/* Contacto */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="telefono">{t("pacientes.fields.phone")}</Label>
                <Input
                  id="telefono"
                  placeholder={isEditing ? undefined : "+504 2222-3333"}
                  className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                  {...register("telefono")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="celular">{t("pacientes.fields.cellphone")}</Label>
                <Input
                  id="celular"
                  placeholder={isEditing ? undefined : "+504 9999-8888"}
                  className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                  {...register("celular")}
                />
              </div>
            </div>

            {/* WhatsApp */}
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="tiene_whatsapp">{t("pacientes.fields.hasWhatsapp")}</Label>
                <p className="text-sm text-muted-foreground">
                  {t("pacientes.fields.hasWhatsappDesc")}
                </p>
              </div>
              <Switch
                id="tiene_whatsapp"
                checked={tieneWhatsapp}
                onCheckedChange={(checked) => setValue("tiene_whatsapp", checked)}
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">{t("pacientes.fields.email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder={isEditing ? undefined : "paciente@email.com"}
                className={!isEditing ? "placeholder:text-muted-foreground/50" : undefined}
                {...register("email")}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{t(errors.email.message as string)}</p>
              )}
            </div>

            {/* Foto del Documento */}
            <div className="space-y-2">
              <Label>{t("pacientes.fields.documentPhoto")}</Label>
              <ImageCapture
                value={fotoDocumento}
                onChange={setFotoDocumento}
                existingUrl={paciente?.foto_documento_url}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
